import { useEffect, useRef } from "react";
import dynamic from "next/dynamic";

const OfficialPlateSearch = dynamic(() => import("@cartbot/plate-search"), {
  ssr: false,
  loading: () => null,
});

const UPSTREAM_BASES = new Set([
  "https://api.cartbot.io",
  "https://api.partbot.io",
]);
const UPSTREAM_QUERY_PATH = "/v1/vehicles/plate-search";
const UPSTREAM_RETRY_PATH = "/v1/vehicles/plate-search-retry";
const LOCAL_QUERY_PATH = "/api/plate-search";
const LOCAL_RETRY_PATH = "/api/plate-search-retry";
const FETCH_PROXY_STATE_KEY = "__partbotPlateSearchFetchProxyState";
const AUTOFOCUS_GUARD_TIMEOUT_MS = 5000;

function resolveRewritePath(input) {
  const rawUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input?.url;

  if (!rawUrl || typeof window === "undefined") {
    return null;
  }

  try {
    const normalizedUrl = new URL(rawUrl, window.location.origin);
    const normalizedPath = normalizedUrl.pathname.replace(/\/+$/, "");

    if (!UPSTREAM_BASES.has(normalizedUrl.origin)) {
      return null;
    }

    if (normalizedPath === UPSTREAM_QUERY_PATH) {
      return LOCAL_QUERY_PATH;
    }

    if (normalizedPath === UPSTREAM_RETRY_PATH) {
      return LOCAL_RETRY_PATH;
    }

    return null;
  } catch {
    return null;
  }
}

function usePlateSearchFetchProxy() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.fetch !== "function") {
      return undefined;
    }

    const state = window[FETCH_PROXY_STATE_KEY] || {
      refCount: 0,
      originalFetch: window.fetch.bind(window),
    };

    window[FETCH_PROXY_STATE_KEY] = state;

    if (state.refCount === 0) {
      state.originalFetch = window.fetch.bind(window);

      window.fetch = (input, init) => {
        const rewritePath = resolveRewritePath(input);

        if (!rewritePath) {
          return state.originalFetch(input, init);
        }

        if (input instanceof Request) {
          return state.originalFetch(new Request(rewritePath, input), init);
        }

        return state.originalFetch(rewritePath, init);
      };
    }

    state.refCount += 1;

    return () => {
      state.refCount = Math.max(0, state.refCount - 1);

      if (state.refCount === 0 && typeof state.originalFetch === "function") {
        window.fetch = state.originalFetch;
      }
    };
  }, []);
}

export default function PlateSearch(props) {
  usePlateSearchFetchProxy();
  const wrapperRef = useRef(null);

  const apiKey = props.apiKey || process.env.NEXT_PUBLIC_PARTBOT_API_KEY;
  const displayMode = props.displayMode || "inline";
  const themeColor = props.themeColor || "#10b981";
  const showBranding = props.showBranding ?? false;
  const inlineAlign = props.inlineAlign || "start";

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const applyHostTweaks = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }

      const hostElements = Array.from(wrapper.children || []);
      hostElements.forEach((host) => {
        if (!(host instanceof HTMLElement)) {
          return;
        }

        if (displayMode === "inline") {
          host.style.setProperty("z-index", "1", "important");
        } else {
          host.style.setProperty("z-index", "2147483647", "important");
        }

        if (showBranding) {
          if (inlineAlign !== "end") {
            return;
          }
        }

        const shadow = host.shadowRoot;
        if (!shadow) {
          return;
        }

        if (!showBranding && !shadow.querySelector("style[data-partbot-hide-branding]")) {
          const brandingStyle = document.createElement("style");
          brandingStyle.setAttribute("data-partbot-hide-branding", "true");
          brandingStyle.textContent = `
            a[href*="use.partbot.io/?ref="] {
              display: none !important;
            }
          `;
          shadow.appendChild(brandingStyle);
        }

        if (
          displayMode === "inline" &&
          inlineAlign === "end" &&
          !shadow.querySelector("style[data-partbot-inline-align-end]")
        ) {
          const alignStyle = document.createElement("style");
          alignStyle.setAttribute("data-partbot-inline-align-end", "true");
          alignStyle.textContent = `
            .inline-container {
              display: flex !important;
              justify-content: flex-end !important;
            }

            .inline-panel {
              margin-left: auto !important;
            }
          `;
          shadow.appendChild(alignStyle);
        }
      });
    };

    applyHostTweaks();

    const observer = new MutationObserver(() => {
      applyHostTweaks();
    });

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => observer.disconnect();
  }, [displayMode, inlineAlign, showBranding]);

  useEffect(() => {
    if (typeof window === "undefined" || displayMode !== "inline") {
      return undefined;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const initialScrollX = window.scrollX;
    const initialScrollY = window.scrollY;
    let guardActive = true;
    let frameId = null;

    const stopGuard = () => {
      guardActive = false;
    };

    const interactionEvents = ["pointerdown", "touchstart", "keydown", "wheel"];
    const handleUserInteraction = () => {
      stopGuard();
    };

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserInteraction, {
        capture: true,
        passive: true,
        once: true,
      });
    });

    const checkAndUndoAutofocus = () => {
      if (!guardActive) {
        return;
      }

      let blurred = false;
      const hostElements = Array.from(wrapper.children || []);
      hostElements.forEach((host) => {
        if (!(host instanceof HTMLElement)) {
          return;
        }

        const shadowActive = host.shadowRoot?.activeElement;
        if (shadowActive instanceof HTMLElement) {
          shadowActive.blur();
          blurred = true;
        }

        if (document.activeElement === host) {
          host.blur();
          blurred = true;
        }
      });

      if (!blurred) {
        return;
      }

      window.scrollTo(initialScrollX, initialScrollY);
    };

    const runGuardLoop = () => {
      checkAndUndoAutofocus();

      if (!guardActive) {
        return;
      }

      frameId = window.requestAnimationFrame(runGuardLoop);
    };

    frameId = window.requestAnimationFrame(runGuardLoop);
    const guardTimeoutId = window.setTimeout(stopGuard, AUTOFOCUS_GUARD_TIMEOUT_MS);

    return () => {
      stopGuard();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.clearTimeout(guardTimeoutId);
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserInteraction, true);
      });
    };
  }, [displayMode]);

  if (!apiKey) {
    return null;
  }

  return (
    <div ref={wrapperRef}>
      <OfficialPlateSearch
        {...props}
        apiKey={apiKey}
        displayMode={displayMode}
        themeColor={themeColor}
        showBranding={showBranding}
      />
    </div>
  );
}
